import React, { useContext, useEffect, useState } from 'react';
import { DatePicker, Form, Select } from 'antd';
import dayjs from 'dayjs';

import { RemoteAccessOverlayNetwork } from '@totem/components/ticketing/remoteAccess';
import { OverlayNetwork } from '@totem/components/ticketing/ticketing';
import Loading from '@totem/components/ui_components/specialCases/Loading';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { isNotNull } from '@totem/utilities/common';
import { REMOTE_ACCESS_ENDPOINT } from '@totem/utilities/endpoints';

type Props = {
  overlayNetwork: OverlayNetwork;
  onChange: (optimize: OverlayNetwork) => void;
};

const RemoteAccessOverlayForm = ({ overlayNetwork, onChange }: Props) => {
  const { getApiToken } = useContext(UserProfileContext);
  const [overlaysLoaded, setOverlaysLoaded] = useState(false);
  const [overlayNetworks, setOverlayNetworks] = useState<
    RemoteAccessOverlayNetwork[]
  >([]);
  const [data, setData] = useState<OverlayNetwork>({
    overlayNetworkId: isNotNull(overlayNetwork)
      ? overlayNetwork.overlayNetworkId
      : '',
    overlayNetworkName: isNotNull(overlayNetwork)
      ? overlayNetwork.overlayNetworkName
      : '',
    startTime: isNotNull(overlayNetwork)
      ? overlayNetwork.startTime
      : dayjs().toISOString(),
    expectedEndTime: isNotNull(overlayNetwork)
      ? overlayNetwork.expectedEndTime
      : dayjs().toISOString(),
    overlayNetworkStatus: 'PENDING',
  });

  useEffect(() => {
    onChange(data);
  }, [data]);

  useEffect(() => {
    fetch(`${REMOTE_ACCESS_ENDPOINT}/overlayNetworks/onDemand`, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
    })
      .then((res) => res.json())
      .then((result: RemoteAccessOverlayNetwork[]) => {
        setOverlayNetworks(result);

        setOverlaysLoaded(true);
      });
  }, [getApiToken]);

  if (!overlaysLoaded) {
    return <Loading />;
  }

  return (
    <>
      <Form.Item label={'Overlay Network'}>
        <Select
          style={{ width: '150px' }}
          value={isNotNull(data) ? data.overlayNetworkId : ''}
          options={
            isNotNull(overlayNetworks) && overlayNetworks.length > 0
              ? overlayNetworks.map((item) => {
                  return {
                    label: item.overlayNetwork.name,
                    value: item.overlayNetwork.id,
                  };
                })
              : []
          }
          onChange={(newValue) => {
            const matched = overlayNetworks.find(
              (item) => item.overlayNetwork.id === newValue,
            );
            setData((prevState) => ({
              ...prevState,
              overlayNetworkId: newValue,
              overlayNetworkName: matched ? matched.overlayNetwork.name : '',
            }));
          }}
        />
      </Form.Item>
      <Form.Item
        label="Start Time"
        name="startDate"
        initialValue={
          isNotNull(data) && data.startTime !== ''
            ? dayjs(data.startTime)
            : dayjs()
        }
        rules={[{ required: true, message: 'Please select date' }]}
      >
        <DatePicker
          showTime={{ defaultValue: dayjs('00:00:00', 'HH:mm:ss') }}
          format={'L LT'}
          style={{ width: '100%' }}
          value={
            isNotNull(data) && data.startTime !== ''
              ? dayjs(data.startTime)
              : dayjs()
          }
          onChange={(newValue) =>
            setData((prevState) => ({
              ...prevState,
              startTime: newValue.toISOString(),
            }))
          }
        />
      </Form.Item>
      <Form.Item
        label="Expected End Time"
        name="endDate"
        initialValue={
          isNotNull(data) && data.expectedEndTime !== ''
            ? dayjs(data.expectedEndTime)
            : dayjs()
        }
        rules={[{ required: true, message: 'Please select date' }]}
      >
        <DatePicker
          showTime={{ defaultValue: dayjs('00:00:00', 'HH:mm:ss') }}
          format={'L LT'}
          style={{ width: '100%' }}
          value={
            isNotNull(data) && data.expectedEndTime !== ''
              ? dayjs(data.expectedEndTime)
              : dayjs()
          }
          onChange={(newValue) =>
            setData((prevState) => ({
              ...prevState,
              expectedEndTime: newValue.toISOString(),
            }))
          }
        />
      </Form.Item>
    </>
  );
};

export default RemoteAccessOverlayForm;
